import { graphql } from '@api/generated';
import { GetCustomerByIdQuery } from '@api/generated/graphql';
import { useQuery } from '@apollo/client/react';
import { FeatureFlagKeys, useFeatureFlag } from '@modules/FeatureFlagModule';

export const getCustomerByIdQuery = graphql(`
  query getCustomerById(
    $tenantId: ID!
    $customerId: ID!
    $includeCustomerInformation: Boolean!
  ) {
    customer(tenantId: $tenantId, id: $customerId) {
      id
      name
      contacts {
        id
        type
        value
        memo
        jobRemindersEnabled
        marketingUpdatesEnabled
        invoiceStatementNotification
      }
      locations {
        id
        active
        name
        address {
          street
          unit
          city
          state
          zip
          country
        }
      }
      tags {
        name
        color
      }
      doNotService
      ... @include(if: $includeCustomerInformation) {
        type
        lifetimeRevenue
        outstandingBalance
        membership
      }
    }
  }
`);

/**
 * Same as {@link getCustomerByIdQuery}, but fetches only a 3-location preview plus the total
 * `locationCount` instead of every location. Used when
 * {@link FeatureFlagKeys.featureCustomerLocationsPreview} is on.
 */
export const getCustomerByIdWithLocationPreviewQuery = graphql(`
  query getCustomerByIdWithLocationPreview(
    $tenantId: ID!
    $customerId: ID!
    $includeCustomerInformation: Boolean!
  ) {
    customer(tenantId: $tenantId, id: $customerId) {
      id
      name
      contacts {
        id
        type
        value
        memo
        jobRemindersEnabled
        marketingUpdatesEnabled
        invoiceStatementNotification
      }
      locationCount
      locations(first: 3) {
        id
        active
        name
        address {
          street
          unit
          city
          state
          zip
          country
        }
      }
      tags {
        name
        color
      }
      doNotService
      ... @include(if: $includeCustomerInformation) {
        type
        lifetimeRevenue
        outstandingBalance
        membership
      }
    }
  }
`);

/**
 * A customer from either query. `locationCount` is only present on the location preview query,
 * where `locations` is capped to 3, so prefer it for totals.
 */
export type CustomerById = NonNullable<GetCustomerByIdQuery['customer']> & {
  locationCount?: number;
};

const useGetCustomerById = (
  tenantId: string,
  customerId?: string,
  includeCustomerInformation: boolean = false,
) => {
  const locationPreviewEnabled = useFeatureFlag(
    FeatureFlagKeys.featureCustomerLocationsPreview,
    { defaultValue: false },
  );
  const options = {
    fetchPolicy: 'network-only',
    variables: {
      tenantId,
      customerId: customerId ?? '',
      includeCustomerInformation,
    },
    notifyOnNetworkStatusChange: true,
  } as const;

  // Both hooks are always called; the inactive one is skipped so only one request is sent.
  const legacy = useQuery(getCustomerByIdQuery, {
    ...options,
    skip: !customerId || locationPreviewEnabled,
  });
  const preview = useQuery(getCustomerByIdWithLocationPreviewQuery, {
    ...options,
    skip: !customerId || !locationPreviewEnabled,
  });

  const { loading, error, refetch } = locationPreviewEnabled ? preview : legacy;

  const customer: CustomerById | null | undefined = locationPreviewEnabled
    ? preview.data?.customer
    : legacy.data?.customer;

  return { customer, loading, error, refetch };
};

export default useGetCustomerById;
